/**
 * Приём видео через Media Source Extensions — единственный транспорт кабинета.
 * Общий для живого просмотра (`lib/player.ts`) и архива (`lib/archive.ts`).
 *
 * ПОЧЕМУ MSE, А НЕ WEBRTC (решение владельца 05.09.2026). Кабинету нужно ровно одно:
 * видеть картинку с камеры и запись из архива, а при надобности открыть дверь.
 * Разговаривать с гостем от компьютера никто не собирается — а двусторонний звук был
 * единственным, ради чего в кабинете стоял WebRTC. Платой за него шли ICE, TURN
 * и отдельные порты UDP: на чётком потоке через интернет картинка рассыпалась
 * (потерянный кадр WebRTC не восстанавливает), а там, где UDP режут — за корпоративным
 * прокси, в гостиничном вайфае, у мобильного оператора со строгим NAT, — просмотр
 * не поднимался вовсе. MSE идёт по 443 тем же соединением, что и остальной кабинет,
 * и буферизует: дрожание сети превращается в задержку, а не в развал картинки.
 *
 * ТРИ ЛОВУШКИ, КОТОРЫЕ ЗДЕСЬ ОБОЙДЕНЫ. Каждая проявляется одинаково — «видео
 * не стартует никогда», — и каждая стоила отладки соседнему проекту (nvrreader)
 * на этих же камерах:
 *
 * 1. **Источник создаётся ТОЛЬКО ПОСЛЕ открытия сокета.** Он открывается сам и сразу,
 *    а список кодеков уходит узлу из его события `sourceopen`. Создай источник раньше —
 *    `sourceopen` случится, пока сокет ещё в состоянии CONNECTING, отправка упадёт,
 *    узел не пришлёт ни одного куска, а повторить отправку негде: второго `sourceopen`
 *    не бывает.
 * 2. **В списке кодеков нет ни одной звуковой дорожки** (см. `CODECS`).
 * 3. **Куски копятся, пока буфер источника занят.** `appendBuffer` во время записи
 *    бросает исключение, а куски идут плотнее, чем браузер их переваривает; выбросить
 *    же кусок нельзя — пропуск рвёт поток навсегда.
 *
 * ПРОТОКОЛ — ПРОТОКОЛ GO2RTC, он проходит через медиа-узел насквозь
 * (`media/verify/src/gate.ts`): клиент шлёт `{"type":"mse","value":"<кодеки>"}`, узел
 * отвечает `{"type":"mse","value":"video/mp4; codecs=…"}`, дальше идут двоичные куски
 * fMP4. Имени потока клиент не знает и не узнаёт: он посылает билет, а имя подставляет
 * узел уже после проверки подписи.
 */

/**
 * Кодеки, которые кабинет готов принять. ТОЛЬКО ВИДЕО, И ЭТО ЛОВУШКА НОМЕР ДВА.
 *
 * Камеры анонсируют звуковую дорожку G.711, по которой не приходит НИ ОДНОГО пакета.
 * Объяви кабинет способность её принять (в go2rtc это `flac`), узел включил бы дорожку
 * в поток — а буферизованный диапазон объединённого буфера есть ПЕРЕСЕЧЕНИЕ дорожек.
 * Пересечение с пустой дорожкой пусто, диапазон не растёт, и видео не начинается
 * никогда. Звука в кабинете нет намеренно (`Проектные_решения.md`, 5.5), поэтому
 * и предлагать нечего — а заодно исчезает вся ловушка целиком.
 *
 * Список предлагаемый, а не требуемый: узел отвечает тем кодеком, который на самом деле
 * у потока (на стенде — `avc1.4D0029`, базовый профиль, которого в списке нет),
 * и буфер создаётся по ответу узла, а не по этой строке.
 */
const CODECS = ['avc1.640029', 'avc1.64002A', 'avc1.640033', 'hvc1.1.6.L153.B0'] as const;

/**
 * Накопитель кусков fMP4, четыре мегабайта.
 *
 * Хватает на несколько секунд чёткого потока — с запасом против любой задержки записи
 * в буфер: одиночный кусок на стенде доходил до 150 КБ.
 */
const CHUNK_BUFFER_BYTES = 4 * 1024 * 1024;

/**
 * Сколько секунд потока держит в буфере живой просмотр.
 *
 * Пять — то же число, что у проигрывателя nvrreader на этих же камерах. Меньше — и
 * дрожание сети снова превращается в фризы, ради ухода от которых транспорт и меняли;
 * больше — и жилец видит двор с заметным опозданием.
 */
const LIVE_BUFFER_SEC = 5;

/**
 * Наименьшая скорость воспроизведения живого потока.
 *
 * Скорость подгоняется так, чтобы идти примерно в секунде от края: отстали — играем
 * быстрее, догнали — медленнее. Ноль ставить нельзя, это остановка; десятая доля —
 * «почти пауза», из которой поток выходит сам, как только придут куски.
 */
const MIN_RATE = 0.1;

/**
 * Сколько секунд записи держит позади точки показа архив.
 *
 * Буфер архива растёт всё окно: пятнадцать минут чёткого потока — сотни мегабайт
 * в памяти вкладки. Пятнадцати секунд позади хватает, чтобы браузер пережил рывок
 * при перерисовке, и не хватает, чтобы память кончилась.
 */
const ARCHIVE_BEHIND_SEC = 15;

/*
 * ЖИВОЕ ВИДЕО В SAFARI ДЕРЖИТСЯ У КРАЯ ИНАЧЕ (замечание владельца 10.10.2026).
 *
 * На iPhone живое видео шло ровно первые секунды, а потом начинало тормозить — и в
 * приложении, и на Wi-Fi, и на LTE. Архив на том же телефоне шёл ровно, Android и кабинет
 * в Chrome — тоже. Транспорт у живого видео и архива один, различие только в том, как
 * держится буфер, — значит, дело в нём. Подгонка, рассчитанная на Chrome, движку Safari
 * (а на iPhone другого нет, встроенный браузер приложения — он же), по-видимому, не подходит;
 * подтверждает или опровергает это только живой iPhone — движка Safari в проверках нет:
 *
 * 1. **Скорость менялась на каждом куске**, то есть с частотой кадров (12–25 раз в секунду),
 *    и дробно: от «почти паузы» до отставания в секундах, то есть и вчетверо, и ввосьмеро.
 *    Chrome меняет темп незаметно; Safari, судя по всему, перестраивает показ на каждой смене.
 * 2. **Показ тянуло к секунде от края.** Safari копит запас перед стартом сам (первый
 *    кадр на iPhone — 2–4 с против 0,3–0,6 с на Android, стенд 10.10.2026), а подгонка
 *    выбирает этот запас, упирает показ в край и затем отыгрывает «почти паузой».
 * 3. **Обрезка шла от края, а не от точки показа.** Удаление по стандарту MSE тянется
 *    до ближайшего ключевого кадра ПОСЛЕ границы, а если его в буфере нет — до конца буфера.
 *    В Chrome показ идёт в секунде от края, и при группе кадров в 2 с удаление до
 *    `край − 5` кончается не дальше `край − 3` — показа не достаёт. В Safari отставание
 *    2–4 с, и то же удаление задевает показываемое, а это по стандарту остановка показа.
 *
 * Поэтому в Safari живое видео держится так же, как архив, плюс мягкое догоняние: скорость
 * обычная и меняется только на границах (см. `safariLiveStep`), обрезается лишь давно
 * показанное. Цена — задержка 2–4 с вместо секунды; на Chrome подгонка прежняя.
 *
 * Safari узнаётся по `ManagedMediaSource`: обычного `MediaSource` на iPhone нет, а у Chrome
 * на 10.10.2026 нет управляемого. Заведёт его Chrome — Android пойдёт этим же шагом: задержка
 * вырастет, но показ останется ровным.
 */

/**
 * Сколько секунд показанного держит позади точки показа Safari.
 *
 * Удаление тянется до ключевого кадра после границы (см. п. 3 выше): тридцати секунд хватает
 * на любую группу кадров наших камер (по `set-streams.mjs` — 2 с, «умный» кодек — до десятка
 * секунд). Памяти это стоит немного: на чётком потоке 6 Мбит/с — около 22 МБ.
 */
const SAFARI_LIVE_BEHIND_SEC = 30;

/** С какого отставания от края Safari начинает догонять. */
const SAFARI_CATCHUP_FROM_SEC = 4;

/** До какого отставания догоняет: столько запаса Safari нужно, чтобы не упираться в край. */
const SAFARI_CATCHUP_TO_SEC = 2.5;

/** Скорость догоняния: на глаз почти незаметна, с четырёх секунд до двух с половиной — за шесть. */
const SAFARI_CATCHUP_RATE = 1.25;

/** С какого отставания не догонять, а прыгнуть к краю: после обрыва связи догонять слишком долго. */
const SAFARI_JUMP_SEC = 8;

/**
 * Сколько запаса впереди нужно, чтобы вернуть показ после недобора.
 *
 * Safari, оставшись без данных, встаёт на паузу и сам из неё не выходит (`embed.ts`,
 * `resumeAfterUnderrun`, iPhone владельца 19.09.2026). Раньше живое видео у края замедлялось
 * до «почти паузы», и до недобора доходило редко; на обычной скорости любой обрыв дольше
 * запаса кончается паузой — и в кабинете, где своего возврата у живого видео нет, она была бы
 * вечной (ревью 10.10.2026). Пущенный сразу, показ через полсекунды снова упёрся бы в край.
 */
const SAFARI_RESUME_SEC = 2;

/** Что сделать с живым буфером в Safari после очередного куска. */
export interface SafariLiveStep {
  /** До какой точки убрать показанное; `null` — убирать нечего. */
  readonly removeTo: number | null;
  /** Куда прыгнуть; `null` — не прыгать. */
  readonly seekTo: number | null;
  /** Скорость показа: 1 или `SAFARI_CATCHUP_RATE`. Медленнее обычной не бывает. */
  readonly rate: number;
  /** Вернуть показ: он стоит после недобора, а запас уже набран. */
  readonly play: boolean;
}

/**
 * Шаг живого буфера в Safari.
 *
 * Скорость переключается с запаздыванием: догоняние начинается с `SAFARI_CATCHUP_FROM_SEC`
 * и идёт до `SAFARI_CATCHUP_TO_SEC`, поэтому скорость меняется дважды за всё догоняние,
 * а не на каждом куске. Признак «уже догоняем» — сама текущая скорость.
 *
 * Прыжок не уходит раньше начала последнего диапазона буфера: иначе после разрыва в потоке
 * он попал бы в дыру и стоял там до следующего прыжка.
 *
 * @param first начало буфера, секунды потока.
 * @param lastStart начало последнего диапазона буфера.
 * @param end край буфера.
 * @param currentTime точка показа.
 * @param rate текущая скорость показа.
 * @param stopped показ стоит (`paused` или `ended`).
 */
export function safariLiveStep(
  first: number,
  lastStart: number,
  end: number,
  currentTime: number,
  rate: number,
  stopped: boolean,
): SafariLiveStep {
  const behind = currentTime - SAFARI_LIVE_BEHIND_SEC;
  const removeTo = behind > first ? behind : null;
  const gap = end - currentTime;
  if (gap > SAFARI_JUMP_SEC) {
    const seekTo = Math.max(end - SAFARI_CATCHUP_TO_SEC, lastStart);
    return { removeTo, seekTo, rate: 1, play: stopped && end - seekTo >= SAFARI_RESUME_SEC };
  }
  const catching = rate > 1 ? gap > SAFARI_CATCHUP_TO_SEC : gap > SAFARI_CATCHUP_FROM_SEC;
  return {
    removeTo,
    seekTo: null,
    rate: catching ? SAFARI_CATCHUP_RATE : 1,
    play: stopped && gap >= SAFARI_RESUME_SEC,
  };
}

/**
 * Что проигрывается. Различие ровно одно — что делать с накопленным буфером.
 *
 * Живое видео держится у края потока: в Chrome буфер подрезается спереди, а скорость
 * подгоняется так, чтобы идти в секунде от края; в Safari — см. `safariLiveStep`. Архив
 * играет ровно с выбранного момента и никуда не прыгает: «догнать край» в записи означает
 * промотать её к концу.
 */
export type MseMode = 'live' | 'archive';

export interface MseHandlers {
  /**
   * Узел закрыл сигнализацию. Код — причина (`media/verify/src/sessions.ts`).
   *
   * Картинка при этом сама не пропадает: в буфере остаются секунды видео. Живой
   * просмотр вызывает `close` тут же — досматривать закрытый поток нечего и нельзя;
   * архив даёт буферу доиграть и ждёт события `ended` на своём элементе.
   */
  readonly onClose?: (code: number) => void;
}

/**
 * Что успело произойти с приёмом. Отчёт на случай «не заиграло».
 *
 * Заведён 06.09.2026: на айфоне архив не запускался, а понять почему было нечем —
 * консоли Safari без Mac нет, а на сервере видно только «клиент закрыл сессию».
 * Догадки здесь стоят дороже фактов.
 */
export interface MseState {
  /** `ManagedMediaSource` (Safari) или обычный `MediaSource`. */
  readonly kind: string;
  /** Что кабинет предложил узлу. */
  readonly offered: string;
  /** Чем узел ответил: настоящий кодек потока. Пусто — ответа не было. */
  readonly answered: string;
  /** Открылся ли источник: без этого не создаётся буфер. */
  readonly opened: boolean;
  /** Сколько двоичных кусков и байтов пришло. */
  readonly chunks: number;
  readonly bytes: number;
  /** Что сказал сам элемент видео. */
  readonly readyState: number;
  readonly buffered: string;
  readonly error: string | null;
  /**
   * Точка показа, длина записи и состояние источника.
   *
   * Заведены 08.09.2026: архив на телефоне обрывался сообщением «запись здесь
   * закончилась», а по журналу узла сигнализация в этот момент была ОТКРЫТА. Без
   * этих трёх чисел нельзя отличить доигранный до конца буфер от источника,
   * закончившегося раньше времени, — а лечение у них разное.
   */
  readonly currentTime: number;
  readonly duration: number;
  readonly sourceState: string;
  /**
   * Сокет сигнализации: `connecting` | `open` | `closing` | `closed` — и сколько
   * миллисекунд он открывался (`null` — не открылся ни разу).
   *
   * Заведены 25.10.2026 по просьбе владельца. У двух iPhone видео временами не шло
   * минутами: билет выдан, а до видеоузла от телефона не доходит ни одного запроса.
   * Сеть это или телефон, по серверу не понять — узел такого зрителя не видит вовсе.
   * «Сокет так и не открылся» отличает эту беду от всех остальных.
   */
  readonly socket: string;
  readonly socketMs: number | null;
  /** На каком шаге стоит приём (`mseStage`). */
  readonly stage: MseStage;
}

/**
 * Шаг, на котором стоит приём, — по порядку, в котором приём их проходит:
 * - `socket` — сокет к видеоузлу не открылся: соединение не дошло до узла;
 * - `source` — сокет открыт, а источник видео в браузере не открылся (кодеки узлу
 *   не предложены);
 * - `answer` — кодеки предложены, узел не ответил: беда у узла или у прибора;
 * - `data` — узел ответил кодеком, а данных нет;
 * - `decode` — данные пришли, а кадр не собрался;
 * - `frame` — кадр есть.
 *
 * Шаг — первый несделанный, и только. Оборвалась ли связь по дороге, видно рядом,
 * по `socket`: `closed` при шаге `source` или `answer` — это обрыв, а не беда браузера
 * или узла; пустой `offered` при шаге `answer` — браузер не взял ни одного кодека.
 */
export type MseStage = 'socket' | 'source' | 'answer' | 'data' | 'decode' | 'frame';

/** Вычисляет шаг приёма по его сводке. */
export function mseStage(state: {
  readonly socketMs: number | null;
  readonly opened: boolean;
  readonly answered: string;
  readonly bytes: number;
  readonly readyState: number;
}): MseStage {
  if (state.socketMs === null) return 'socket';
  if (!state.opened) return 'source';
  if (state.answered === '') return 'answer';
  if (state.bytes === 0) return 'data';
  // HAVE_CURRENT_DATA (2): у элемента есть кадр для текущей точки показа.
  if (state.readyState < 2) return 'decode';
  return 'frame';
}

/** Состояние сокета словом — по номеру `WebSocket.readyState`. */
const SOCKET_STATES = ['connecting', 'open', 'closing', 'closed'];

export interface MseConnection {
  /** Послать узлу своё сообщение (`umkey/alive`, `umkey/stats`). Закрытый сокет молчит. */
  readonly send: (message: unknown) => void;
  /** Сводка приёма — для отчёта, когда видео не пошло. */
  readonly state: () => MseState;
  /**
   * Разобрать всё: сокет, источник и элемент видео. Повторный вызов безвреден.
   *
   * ЭТО НЕ ТОЛЬКО УБОРКА, А ИСПОЛНЕНИЕ ОТЗЫВА: отвязанный источник перестаёт принимать
   * куски, и последний кадр уходит с экрана — иначе двор оставался бы на нём под
   * надписью «Доступ отозван».
   */
  readonly close: () => void;
}

/**
 * Умеет ли этот браузер играть fMP4 через MSE.
 *
 * Проверяется ДО билета: браузеру, который не покажет ничего, билет не нужен — иначе
 * он занял бы слот просмотра и держал бы его до потолка сессии. На iPhone обычного
 * `MediaSource` нет вовсе (до iOS 17.1 не было и `ManagedMediaSource`), и это
 * единственный случай, когда кабинет честно говорит, что показать нечем.
 */
export function mseReady(): boolean {
  return sourceClass() !== null;
}

/**
 * Откроет ли ЭТОТ браузер поток с таким кодеком.
 *
 * Спрашивается у браузера, а не выводится из имени кодека, и это единственный честный
 * способ. H.265 открывается там, где есть аппаратный декодер, и не открывается там,
 * где его нет: на обычной машине Chrome его играет, в контейнере без декодера — нет.
 * Сервер этого знать не может и потому больше не решает (`services/media/cameras.ts`).
 *
 * Неизвестный кодек (`null`, пустая строка) считается играющим: пустое поле означает
 * «не снято с прибора», а не «плохой кодек». Непригодность обязана быть доказана.
 */
export function canPlayCodec(codec: string | null): boolean {
  if (codec === null || codec.trim() === '') return true;
  const Source = sourceClass();
  // MSE в браузере нет вовсе — тогда не откроется НИЧЕГО, и об этом сказано отдельным
  // состоянием (`mseReady`). Отвечать здесь «дело в кодеке» значило бы дать второе
  // объяснение вместо верного.
  if (Source === null) return true;
  const plain = codec.replace(/[.\-\s]/gu, '').toUpperCase();
  // Строки те же, что в предлагаемом списке: спрашиваем ровно про то, что запросим.
  if (plain === 'H264') return Source.isTypeSupported('video/mp4; codecs="avc1.640029"');
  if (plain === 'H265' || plain === 'HEVC') {
    return Source.isTypeSupported('video/mp4; codecs="hvc1.1.6.L153.B0"');
  }
  // Прочее наш узел в fMP4 не упакует — но это решает сервер, а не браузер.
  return true;
}

/**
 * Разновидность `MediaSource` в этом браузере.
 *
 * Safari отдаёт `ManagedMediaSource` — он сам просит и придерживает данные — и обычного
 * `MediaSource` на телефоне у него нет вовсе. В типах браузера `ManagedMediaSource`
 * пока нет, отсюда приведение.
 */
function sourceClass(): typeof MediaSource | null {
  const scope = window as unknown as {
    ManagedMediaSource?: typeof MediaSource;
    MediaSource?: typeof MediaSource;
  };
  return scope.ManagedMediaSource ?? scope.MediaSource ?? null;
}

/**
 * Открывает приём видео и привязывает его к окну.
 *
 * @param url адрес сигнализации вместе с билетом — тот, что пришёл в ответе на билет.
 * @param video окно просмотра. Приходит снаружи: элемент рисует экран.
 * @param mode живое видео или архив (см. `MseMode`).
 */
export function connectMse(
  url: string,
  video: HTMLVideoElement,
  mode: MseMode,
  handlers: MseHandlers = {},
  /**
   * Во сколько раз быстрее показывать запись (только архив).
   *
   * Метки времени в ускоренном потоке идут «как записано» — прибор отдаёт те же кадры
   * с теми же отметками, просто быстрее. Значит окно надо и ПОКАЗЫВАТЬ быстрее: оставь
   * мы единицу, кадры приходили бы вчетверо быстрее показа, буфер рос бы всё окно,
   * а человек видел бы обычную скорость и недоумевал, за что нажимал ×4.
   */
  rate: 1 | 2 | 4 = 1,
): MseConnection {
  const Source = sourceClass();
  /** Safari (см. «Живое видео в Safari» выше): живой буфер держится по `safariLiveStep`. */
  const managed = 'ManagedMediaSource' in window;
  /**
   * Когда сокет начал открываться: от этого мига считается `socketMs`. Часы монотонные:
   * по `Date.now()` перевод часов назад дал бы отрицательное время, и сервер отверг бы
   * отчёт целиком.
   */
  const startedAt = performance.now();
  const socket = new WebSocket(url);
  // Куски fMP4 нужны готовым буфером: разбирать `Blob` пришлось бы обещанием,
  // и куски перемешались бы между собой.
  socket.binaryType = 'arraybuffer';

  /** Разобрано ли соединение нами. После этого ни событий, ни состояний. */
  let disposed = false;
  /** Сводка приёма — заполняется по ходу, читается отчётом при отказе. */
  let offered = '';
  let answered = '';
  let opened = false;
  let chunks = 0;
  let bytes = 0;
  /** Сколько сокет открывался; `null` — не открылся. */
  let socketMs: number | null = null;
  let source: MediaSource | null = null;
  let buffer: SourceBuffer | null = null;
  let blobUrl: string | null = null;
  /** Узел закрыл сигнализацию: остаётся дописать накопленное и закончить источник. */
  let ending = false;

  /** Куски, пришедшие, пока буфер занят или ещё не создан (ловушка 3). */
  const waiting = new Uint8Array(CHUNK_BUFFER_BYTES);
  let waitingLength = 0;

  /**
   * Управление накопленным буфером. Вызывается после каждого освобождения буфера —
   * это единственный момент, когда с ним можно что-то делать.
   */
  const trim = (sb: SourceBuffer): void => {
    if (sb.buffered.length === 0) return;
    const first = sb.buffered.start(0);
    const end = sb.buffered.end(sb.buffered.length - 1);

    if (mode === 'archive') {
      // Запись играет как есть: ни прыжков к краю, ни подгонки скорости под отставание.
      // Скорость у архива задаёт ЧЕЛОВЕК, и она ровно та, с какой узел отдаёт запись;
      // ставится здесь, а не один раз при открытии, потому что смена источника
      // и `load()` возвращают элементу единицу.
      if (video.playbackRate !== rate) video.playbackRate = rate;
      // Убирается только то, что осталось далеко позади, — иначе буфер растёт все
      // пятнадцать минут окна и съедает память вкладки.
      const behind = video.currentTime - ARCHIVE_BEHIND_SEC;
      if (behind > first) {
        try {
          sb.remove(first, behind);
        } catch {
          // Буфер снова занят — обрежем на следующем освобождении.
        }
      }
      return;
    }

    if (managed) {
      const step = safariLiveStep(
        first,
        sb.buffered.start(sb.buffered.length - 1),
        end,
        video.currentTime,
        video.playbackRate,
        video.paused || video.ended,
      );
      if (step.removeTo !== null) {
        try {
          sb.remove(first, step.removeTo);
        } catch {
          // Буфер снова занят — обрежем на следующем освобождении.
        }
      }
      if (step.seekTo !== null) video.currentTime = step.seekTo;
      // Только при смене: каждое присваивание, по-видимому, перестраивает показ Safari.
      if (video.playbackRate !== step.rate) video.playbackRate = step.rate;
      if (step.play) {
        const wasAt = video.currentTime;
        void Promise.resolve(video.play())
          .then(() => {
            // «Доигранный» показ часть движков возвращает с НАЧАЛА буфера — назад в прошлое;
            // возвращаем точку туда, где она стояла (так же делает `embed.ts`).
            if (video.currentTime < wasAt - 1) video.currentTime = wasAt;
          })
          .catch(() => undefined);
      }
      return;
    }

    const keep = end - LIVE_BUFFER_SEC;
    if (keep > first) {
      try {
        sb.remove(first, keep);
      } catch {
        // То же: обрежем на следующем освобождении.
      }
      if (source !== null && source.readyState === 'open') {
        try {
          source.setLiveSeekableRange(keep, end);
        } catch {
          // Источник уже закрыт: границы проматывания больше никому не нужны.
        }
      }
    }
    // Точка показа, отставшая дальше буфера, — это дыра: там уже ничего нет.
    if (video.currentTime < keep) video.currentTime = keep;
    // Скорость подгоняется к отставанию от края: отстали на три секунды — играем втрое
    // быстрее и догоняем, ушли к самому краю — играем медленнее и ждём. Равновесие
    // приходится ровно на секунду отставания. Это и есть замена фризам: дрожание сети
    // превращается в незаметное изменение темпа, а не в остановку кадра.
    const gap = end - video.currentTime;
    video.playbackRate = gap > MIN_RATE ? gap : MIN_RATE;
  };

  /**
   * Закончить источник, когда сигнализация закрыта, а всё пришедшее дописано.
   *
   * Без этого элемент замер бы на последнем кадре, ожидая продолжения, которого
   * не будет: буфер кончился, а `ended` без `endOfStream` не приходит. Вызов во время
   * записи в буфер бросает исключение, поэтому его повторяет каждое освобождение
   * буфера, пока не пройдёт.
   */
  const finish = (): void => {
    if (!ending || source?.readyState !== 'open') return;
    if (waitingLength > 0) return;
    if (buffer?.updating === true) return;
    try {
      source.endOfStream();
    } catch {
      // Источник успел закрыться сам — доигрывать всё равно нечего.
    }
  };

  /** Дописать накопленное, прибрать буфер и, если пора, закончить источник. */
  const drain = (): void => {
    const sb = buffer;
    if (sb === null || sb.updating) return;
    if (waitingLength > 0) {
      const ready = waiting.slice(0, waitingLength);
      waitingLength = 0;
      try {
        sb.appendBuffer(ready);
        return;
      } catch {
        // Буфер полон или источник закрылся: следующая запись пойдёт с чистого места.
      }
    }
    trim(sb);
    finish();
  };

  const appendChunk = (chunk: ArrayBuffer): void => {
    chunks += 1;
    bytes += chunk.byteLength;
    const data = new Uint8Array(chunk);
    const sb = buffer;
    if (sb !== null && !sb.updating && waitingLength === 0) {
      try {
        sb.appendBuffer(data);
        return;
      } catch {
        // Буфер не принял — кусок отправляется в накопитель, как все прочие.
      }
    }
    if (data.byteLength > waiting.length) return;
    // Переполнение накопителя означает, что браузер безнадёжно отстал: собирать
    // из обрывков нечего, начинаем копить заново с текущего куска.
    if (waitingLength + data.byteLength > waiting.length) waitingLength = 0;
    waiting.set(data, waitingLength);
    waitingLength += data.byteLength;
  };

  const close = (): void => {
    if (disposed) return;
    disposed = true;
    socket.close();
    buffer = null;
    source = null;
    waitingLength = 0;
    video.pause();
    video.srcObject = null;
    video.removeAttribute('src');
    // Без `load` элемент остаётся с прежним источником, и следующее подключение
    // показывало бы кадры предыдущего.
    video.load();
    // Скорость подгоняет живое видео; следующему просмотру она достаться не должна.
    video.playbackRate = 1;
    if (blobUrl !== null) {
      URL.revokeObjectURL(blobUrl);
      blobUrl = null;
    }
  };

  socket.onopen = () => {
    socketMs = Math.round(performance.now() - startedAt);
    if (disposed || Source === null) return;
    // ЛОВУШКА 1: источник создаётся здесь, а не раньше — сокет уже открыт.
    const ms = new Source();
    source = ms;
    ms.addEventListener(
      'sourceopen',
      () => {
        if (disposed || socket.readyState !== WebSocket.OPEN) return;
        const list = CODECS.filter((codec) =>
          Source.isTypeSupported(`video/mp4; codecs="${codec}"`),
        ).join(',');
        offered = list;
        opened = true;
        socket.send(JSON.stringify({ type: 'mse', value: list }));
      },
      { once: true },
    );

    if ('ManagedMediaSource' in window) {
      // Safari принимает `ManagedMediaSource` только объектом и требует отключить
      // передачу на внешний экран: иначе источник закрывается сразу после привязки.
      video.disableRemotePlayback = true;
      video.srcObject = ms;
    } else {
      blobUrl = URL.createObjectURL(ms);
      video.src = blobUrl;
    }

    /**
     * Отказ запуска показа БОЛЬШЕ НЕ ЗАМАЛЧИВАЕТСЯ.
     *
     * Раньше он глотался: считалось, что при `autoplay` браузер и сам справится.
     * На iPhone это скрыло главное звено — `ManagedMediaSource` бережливый
     * и открывается, только когда элемент ДЕЙСТВИТЕЛЬНО начинает играть. Не пошёл
     * показ — источник остаётся закрытым, поток у узла никто не просит, и окно
     * чернеет без единой ошибки: «источник closed, кусков 0, байт 0» (iPhone 15 Pro
     * владельца, 19.09.2026). Сообщение уходит в консоль страницы, а оттуда —
     * в журнал телефона и в служебную метку окна.
     */
    void Promise.resolve(video.play()).catch((error: unknown) => {
      console.log(`показ не запустился: ${String(error)}`);
    });

    /**
     * Источник не открылся за две секунды — говорим об этом вслух.
     *
     * Событие `sourceopen` — условие всего дальнейшего: пока его нет, кодеки узлу
     * не предложены и данные не идут. Молчаливое ожидание здесь выглядит как
     * «всё хорошо, просто чёрный экран», и именно так оно и выглядело.
     */
    setTimeout(() => {
      if (disposed || opened) return;
      console.log(
        `источник не открылся: состояние ${ms.readyState}, ` +
          `видео ${String(video.readyState)}, ` +
          (video.paused ? 'на паузе, ' : 'играет, ') +
          `привязка ${video.srcObject === null ? 'ссылкой' : 'объектом'}` +
          `, страница ${document.visibilityState}` +
          (video.error === null ? '' : `, ошибка элемента ${String(video.error.code)}`),
      );
    }, 2000);
  };

  socket.onmessage = (event: MessageEvent<unknown>) => {
    if (disposed) return;
    if (typeof event.data !== 'string') {
      appendChunk(event.data as ArrayBuffer);
      return;
    }
    let message: { type?: unknown; value?: unknown };
    try {
      message = JSON.parse(event.data) as { type?: unknown; value?: unknown };
    } catch {
      return;
    }
    // Ответ узла с настоящим кодеком потока — единственное текстовое сообщение,
    // на которое кабинету надо отвечать действием. Ошибку узел присылает обезличенной
    // (`{"type":"error"}`) и следом закрывает сигнализацию: её разбирает `onClose`.
    if (message.type !== 'mse' || typeof message.value !== 'string') return;
    answered = message.value;
    const ms = source;
    // Второго ответа с кодеком узел не шлёт; если пришлёт — второй буфер к тому же
    // источнику добавить нельзя, и попытка кончилась бы ошибкой на ровном месте.
    if (ms === null || buffer !== null) return;
    try {
      const created = ms.addSourceBuffer(message.value);
      // `segments`: куски приходят со своими метками времени, и складывать их подряд,
      // как в `sequence`, значило бы врать о времени внутри потока.
      created.mode = 'segments';
      created.addEventListener('updateend', drain);
      buffer = created;
      // Куски, пришедшие раньше ответа с кодеком, уже накоплены — начинаем с них:
      // первый из них и есть заголовок потока, без которого не собрать ничего.
      drain();
    } catch {
      // Браузер не берёт кодек, который выбрал узел. Кадров не будет; экран узнает
      // об этом закрытием сигнализации, которое последует за нашим `close`.
      close();
    }
  };

  socket.onclose = (event) => {
    if (disposed) return;
    // Конец записи — не обрыв: в буфере ещё лежат секунды видео. `endOfStream` даёт
    // элементу их доиграть и закончить событием `ended`. Живому просмотру это ни к чему,
    // и он закрывает соединение прямо в обработчике — до `ended` дело не доходит.
    ending = true;
    finish();
    handlers.onClose?.(event.code);
  };

  return {
    send: (message: unknown): void => {
      if (socket.readyState !== WebSocket.OPEN) return;
      socket.send(JSON.stringify(message));
    },
    state: (): MseState => {
      const ranges: string[] = [];
      for (let i = 0; i < video.buffered.length; i += 1) {
        ranges.push(`${video.buffered.start(i).toFixed(1)}–${video.buffered.end(i).toFixed(1)}`);
      }
      return {
        currentTime: Number(video.currentTime.toFixed(2)),
        // `duration` у источника — конец последнего дописанного куска, и NaN до первого.
        duration: Number.isFinite(video.duration) ? Number(video.duration.toFixed(2)) : -1,
        sourceState: source?.readyState ?? 'none',
        kind: 'ManagedMediaSource' in window ? 'managed' : 'plain',
        offered,
        answered,
        opened,
        chunks,
        bytes,
        readyState: video.readyState,
        // Диапазоны буфера строкой: по ним видно и «ничего не пришло», и «пришло,
        // но начинается не с нуля» — а это разные болезни с разным лечением.
        // Длины — те же, что принимает сервер (`reportBody`): длиннее — и отчёт отвергнут
        // целиком, молча. У Chrome на Android текст ошибки разбора бывает длиннее 200.
        buffered: (ranges.join(' ') || '—').slice(0, 200),
        // Проверка на «пусто», а не на `null`: в браузере здесь `null`, а вне его
        // (в проверках) свойства может не быть вовсе — падать на отчёте об отказе
        // было бы вдвойне обидно.
        error:
          video.error == null
            ? null
            : `${String(video.error.code)}: ${video.error.message}`.slice(0, 200),
        socket: SOCKET_STATES[socket.readyState] ?? String(socket.readyState),
        socketMs,
        stage: mseStage({ socketMs, opened, answered, bytes, readyState: video.readyState }),
      };
    },
    close,
  };
}
